import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, Pencil, Loader2, AlertCircle, Search, X, RefreshCw, CheckCircle2, Users as UsersIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import adminService from '@/services/adminService.ts';
import type { AdminUserRole, AdminUserSummary, ManagerSummary, ResetSalespersonStatsResponse } from '@/types/admin.ts';
import { formatDate } from '@/utils/formatDate.ts';
import CreateManagerModal from '@/components/superuser/admin/CreateManagerModal.tsx';
import ChangeCompanyModal from '@/components/superuser/admin/ChangeCompanyModal.tsx';
import ResetSalespersonStatsModal from '@/components/superuser/admin/ResetSalespersonStatsModal.tsx';

const ALL_COMPANIES = '__all__';
const NO_COMPANY = '__none__';

type RoleTab = 'all' | 'SalesManager' | 'Salesperson';

function toManagerSummary(u: AdminUserSummary): ManagerSummary {
  return {
    id: u.id,
    email: u.email,
    firstName: u.firstName,
    lastName: u.lastName,
    company: u.company,
    teamCount: u.teamCount ?? 0,
    salespersonCount: u.salespersonCount ?? 0,
    createdAt: u.createdAt,
  };
}

export default function SuperUserAdminUsersPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [companyTarget, setCompanyTarget] = useState<ManagerSummary | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUserSummary | null>(null);
  const [resetFlash, setResetFlash] = useState<{ name: string; deleted: number; created: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>(ALL_COMPANIES);
  const [roleTab, setRoleTab] = useState<RoleTab>('all');

  const roleFilter: AdminUserRole | undefined = roleTab === 'all' ? undefined : roleTab;

  const { data, loading, error, refetch } = useAsyncData<AdminUserSummary[]>(
    () => adminService.listUsers(roleFilter),
    [roleTab],
    { initial: [] },
  );

  const users = data ?? [];

  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    let hasUnassigned = false;
    for (const u of users) {
      if (u.company) set.add(u.company);
      else hasUnassigned = true;
    }
    return {
      values: Array.from(set).sort((a, b) => a.localeCompare(b)),
      hasUnassigned,
    };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return users.filter((u) => {
      if (companyFilter === NO_COMPANY && u.company) return false;
      if (companyFilter !== ALL_COMPANIES && companyFilter !== NO_COMPANY && u.company !== companyFilter) return false;
      if (!needle) return true;
      const haystack = `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [users, searchTerm, companyFilter]);

  const hasActiveFilters = searchTerm.trim().length > 0 || companyFilter !== ALL_COMPANIES;
  const clearFilters = () => {
    setSearchTerm('');
    setCompanyFilter(ALL_COMPANIES);
  };

  const tabs: { id: RoleTab; label: string }[] = [
    { id: 'all', label: t('superuser.admin.users.tabs.all', { defaultValue: 'Todos' }) },
    { id: 'SalesManager', label: t('superuser.admin.users.tabs.managers', { defaultValue: 'Managers' }) },
    { id: 'Salesperson', label: t('superuser.admin.users.tabs.salespeople', { defaultValue: 'Vendedores' }) },
  ];

  async function handleResetDone(result: ResetSalespersonStatsResponse) {
    const target = resetTarget;
    setResetTarget(null);
    if (target) {
      setResetFlash({
        name: `${target.firstName} ${target.lastName}`,
        deleted: result.deletedSessionCount,
        created: result.createdSessionCount,
      });
      window.setTimeout(() => setResetFlash(null), 6000);
    }
    await refetch();
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading text-foreground">
            {t('superuser.admin.users.title', { defaultValue: 'Admin · Usuarios' })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('superuser.admin.users.subtitle', {
              defaultValue: 'Crea Managers, reasigna empresas y gestiona vendedores. Cambios se registran en el log.',
            })}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="shrink-0">
          <Plus className="size-4" />
          {t('superuser.admin.users.createButton', { defaultValue: 'Crear Manager' })}
        </Button>
      </div>

      {resetFlash && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t('superuser.admin.users.resetFlash', {
              name: resetFlash.name,
              deleted: resetFlash.deleted,
              created: resetFlash.created,
              defaultValue: 'Stats reseteadas para {{name}}: {{deleted}} sesión(es) borrada(s), {{created}} nueva(s) generada(s).',
            })}
          </span>
        </div>
      )}

      <div className="mb-4 inline-flex rounded-lg border border-border bg-card p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setRoleTab(tab.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              roleTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!loading && !error && users.length > 0 && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[16rem]">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t('superuser.admin.users.filters.searchLabel', { defaultValue: 'Buscar' })}
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('superuser.admin.users.filters.searchPlaceholder', { defaultValue: 'Nombre o email' })}
                className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="min-w-[12rem]">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t('superuser.admin.users.filters.companyLabel', { defaultValue: 'Empresa' })}
            </label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value={ALL_COMPANIES}>
                {t('superuser.admin.users.filters.companyAll', { defaultValue: 'Todas' })}
              </option>
              {companyOptions.hasUnassigned && (
                <option value={NO_COMPANY}>
                  {t('superuser.admin.users.filters.companyNone', { defaultValue: 'Sin asignar' })}
                </option>
              )}
              {companyOptions.values.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="size-3.5" />
              {t('superuser.admin.users.filters.clear', { defaultValue: 'Limpiar' })}
            </Button>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('superuser.admin.users.loading', { defaultValue: 'Cargando usuarios…' })}
        </div>
      )}

      {error && !loading && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {t('superuser.admin.users.emptyAll', { defaultValue: 'No hay usuarios todavía.' })}
        </div>
      )}

      {!loading && !error && users.length > 0 && filteredUsers.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {t('superuser.admin.users.filters.noMatches', {
            defaultValue: 'Ningún usuario coincide con los filtros.',
          })}
        </div>
      )}

      {!loading && !error && filteredUsers.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.admin.users.col.user', { defaultValue: 'Usuario' })}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.admin.users.col.role', { defaultValue: 'Rol' })}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.admin.users.col.email', { defaultValue: 'Email' })}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.admin.users.col.company', { defaultValue: 'Empresa' })}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.admin.users.col.context', { defaultValue: 'Equipos / Sesiones' })}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.admin.users.col.createdAt', { defaultValue: 'Alta' })}
                </th>
                <th className="px-4 py-3 text-right font-medium w-[12rem]" />
                <th className="px-4 py-3 text-right font-medium w-[10rem]" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers.map((u) => {
                const isManager = u.role === 'SalesManager';
                return (
                  <tr key={u.id} className="text-foreground">
                    <td className="px-4 py-3">{u.firstName} {u.lastName}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        isManager
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {isManager
                          ? t('superuser.admin.users.role.manager', { defaultValue: 'Manager' })
                          : t('superuser.admin.users.role.salesperson', { defaultValue: 'Vendedor' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.company ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                          <Building2 className="size-3" />
                          {u.company}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t('superuser.admin.users.noCompany', { defaultValue: 'Sin asignar' })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {isManager ? (
                        <span className="inline-flex items-center gap-2">
                          <UsersIcon className="size-3" />
                          {t('superuser.admin.users.managerContext', {
                            teams: u.teamCount ?? 0,
                            people: u.salespersonCount ?? 0,
                            sessions: u.sessionCount ?? 0,
                            defaultValue: '{{teams}} equipo(s) · {{people}} vendedor(es) · {{sessions}} sesión(es)',
                          })}
                        </span>
                      ) : (
                        <span>
                          {u.teamName
                            ? t('superuser.admin.users.salespersonContext', {
                                team: u.teamName,
                                sessions: u.sessionCount ?? 0,
                                defaultValue: 'Equipo «{{team}}» · {{sessions}} sesión(es)',
                              })
                            : t('superuser.admin.users.salespersonNoTeamContext', {
                                sessions: u.sessionCount ?? 0,
                                defaultValue: 'Sin equipo · {{sessions}} sesión(es)',
                              })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      {isManager && (
                        <Button variant="ghost" size="xs" onClick={() => setCompanyTarget(toManagerSummary(u))}>
                          <Pencil className="size-3" />
                          {t('superuser.admin.users.changeCompanyButton', { defaultValue: 'Cambiar empresa' })}
                        </Button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="xs" onClick={() => setResetTarget(u)}>
                        <RefreshCw className="size-3" />
                        {t('superuser.admin.users.resetButton', { defaultValue: 'Reset stats' })}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateManagerModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          setCreateOpen(false);
          await refetch();
        }}
      />

      <ChangeCompanyModal
        target={companyTarget}
        onClose={() => setCompanyTarget(null)}
        onSaved={async () => {
          setCompanyTarget(null);
          await refetch();
        }}
      />

      <ResetSalespersonStatsModal
        target={resetTarget}
        onClose={() => setResetTarget(null)}
        onDone={handleResetDone}
      />
    </div>
  );
}
